/* eslint-disable react-refresh/only-export-components */
import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { onAuthStateChanged, signInAnonymously, User } from "firebase/auth";
import { db, auth } from "@/firebase";
import { getDoc, setDoc, updateDoc, doc } from "firebase/firestore";
import { UserRecord } from "@/types/User";
import { userConverter } from "@/helpers/firestore_converters";
import { generateGuestName } from "@/helpers/guestNameGenerator";
import { realtimeManager } from "@/services/realtime/RealtimeManager";
import { RealtimeListener, RealtimePayload } from "@/services/realtime/types";
import { useAlertsStore } from "./AlertsStore";
import { useConnectedRobotsStore } from "./ConnectedRobotsStore";
import { useFeatureFlagStore } from "./FeatureFlagStore";
import { Peerbot } from "@/types/Robots/Peerbot";
import i18n from "@/i18n/i18n";

const GUEST_NAME_KEY = "peerbots_guest_name";

function getInitialGuestName(): string {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(GUEST_NAME_KEY);
      if (stored && stored.trim()) {
        return stored.trim();
      }
      const generated = generateGuestName();
      localStorage.setItem(GUEST_NAME_KEY, generated);
      return generated;
    } catch {
      return generateGuestName();
    }
  }
  return generateGuestName();
}

export const FirebaseAuthContext = createContext<{
  user: User | null;
  userInDb: UserRecord | null;
  guestName: string;
  isGuest: boolean;
  regenerateGuestName: () => string;
  identifier: string | null;
  activeChannels: string[];
  pubnubSubs: string[];
}>({
  user: null,
  userInDb: null,
  guestName: "",
  isGuest: true,
  regenerateGuestName: () => "",
  identifier: null,
  activeChannels: [],
  pubnubSubs: [],
});

export const useFirebaseAuth = () => useContext(FirebaseAuthContext);

export const FirebaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userInDb, setUserInDb] = useState<UserRecord | null>(null);
  const [guestName, setGuestName] = useState<string>(getInitialGuestName);
  const [pubnubSubs, setPubnubSubs] = useState<string[]>([]);

  const [authReady, setAuthReady] = useState<boolean>(false);

  const addAlert = useAlertsStore((state) => state.addAlert);
  const connectRobot = useConnectedRobotsStore((state) => state.connect);

  const isGuest = user == null || user.isAnonymous;

  const regenerateGuestName = useCallback(() => {
    const newName = generateGuestName();
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(GUEST_NAME_KEY, newName);
      } catch (e) {
        console.warn("Could not save guest name to localStorage:", e);
      }
    }
    setGuestName(newName);
    return newName;
  }, []);

  const determineThisUserIdentifier = useCallback(() => {
    if (user && !user.isAnonymous) {
      if (userInDb?.data.username) {
        return userInDb.data.username;
      }
      if (user.email) {
        return user.email;
      }
      return user.uid;
    }
    return guestName;
  }, [guestName, user, userInDb]);

  const identifier = determineThisUserIdentifier();

  const activeChannels = useMemo(() => {
    if (user && !user.isAnonymous) {
      const channels: string[] = [];
      if (user.email) {
        channels.push(user.email.toLowerCase());
      }
      if (userInDb?.data.username) {
        const uname = userInDb.data.username.toLowerCase();
        if (!channels.includes(uname)) {
          channels.push(uname);
        }
      }
      if (channels.length === 0 && user.uid) {
        channels.push(user.uid.toLowerCase());
      }
      return channels;
    }
    return guestName ? [guestName.toLowerCase()] : [];
  }, [user, userInDb, guestName]);

  const alertMessageListener = useMemo<RealtimeListener>(
    () => ({
      onMessage: (msg: RealtimePayload) => {
        const anyMsg = msg as unknown as Record<string, unknown>;
        if (
          anyMsg.type === "Add Connection" &&
          anyMsg.robot === "Peerbot" &&
          anyMsg.to
        ) {
          addAlert({
            level: "Info",
            message: i18n.t("alerts.incomingKnock", {
              from: String(anyMsg.to),
            }),
            duration: 30000,
            action: {
              name: i18n.t("alerts.connectAction"),
              callback: () => {
                const callerInfo = determineThisUserIdentifier();
                if (callerInfo !== null) {
                  connectRobot(
                    new Peerbot(anyMsg.to.toLowerCase(), callerInfo),
                  );
                }
              },
            },
          });
        }
      },
    }),
    [addAlert, connectRobot, determineThisUserIdentifier],
  );

  useEffect(() => {
    const unlisten = onAuthStateChanged(auth, (u) => {
      if (u) {
        setUser(u);
        realtimeManager.setTokenGetter?.(async () => u.getIdToken());
        setAuthReady(true);
      } else {
        setUser(null);
        // Automatic anonymous sign in if not logged in
        signInAnonymously(auth)
          .then((cred) => {
            if (cred?.user) {
              setUser(cred.user);
              realtimeManager.setTokenGetter?.(async () =>
                cred.user.getIdToken(),
              );
            }
            setAuthReady(true);
          })
          .catch((err) => {
            console.debug("Anonymous authentication skipped:", err.message);
            setAuthReady(true);
          });
      }
    });

    return () => {
      unlisten();
    };
  }, []);

  useEffect(() => {
    const fetchUserInDb = async (userToFetch: User | null) => {
      if (userToFetch && !userToFetch.isAnonymous) {
        const docSnap = await getDoc(
          doc(db, "users", userToFetch.uid).withConverter(userConverter),
        );
        if (docSnap.exists()) {
          const fetchedUser = docSnap.data();
          await updateDoc(doc(db, "users", userToFetch.uid), {
            name: userToFetch.displayName,
            photoUrl: userToFetch.photoURL,
          });
          setUserInDb(fetchedUser);
        } else {
          await setDoc(doc(db, "users", userToFetch.uid), {
            name: userToFetch.displayName,
            photoUrl: userToFetch.photoURL,
            email: userToFetch.email,
          });
          setUserInDb({
            id: userToFetch.uid,
            data: {
              ...(userToFetch.email && { email: userToFetch.email }),
              ...(userToFetch.displayName && { name: userToFetch.displayName }),
              ...(userToFetch.photoURL && {
                photoUrl: userToFetch.photoURL,
              }),
            },
          });
        }
      } else {
        setUserInDb(null);
      }
    };
    fetchUserInDb(user);
  }, [user]);

  const syncFromFirestore = useFeatureFlagStore(
    (state) => state.syncFromFirestore,
  );
  useEffect(() => {
    if (userInDb) {
      syncFromFirestore(userInDb.data.featureFlags);
    }
  }, [userInDb, syncFromFirestore]);

  useEffect(() => {
    // Wait until auth state is determined to prevent prematurely subscribing with guestName
    if (!authReady && !user) {
      return;
    }

    if (identifier) {
      realtimeManager.setIdentifier?.(identifier);
    }
    realtimeManager.subscribe(activeChannels);
    realtimeManager.addListener(alertMessageListener);
    const timer = setTimeout(() => setPubnubSubs(activeChannels), 0);

    return () => {
      clearTimeout(timer);
      realtimeManager.unsubscribe(activeChannels);
      realtimeManager.removeListener(alertMessageListener);
    };
  }, [authReady, user, activeChannels, identifier, alertMessageListener]);

  return (
    <FirebaseAuthContext.Provider
      value={{
        user,
        userInDb,
        guestName,
        isGuest,
        regenerateGuestName,
        identifier,
        activeChannels,
        pubnubSubs,
      }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  );
};
